/** Gem Power, threshold bonuses, socketing and fusing (PLAN §11 "Gem sockets"). Pure. */
import type { ClassId, Role } from '@keystone/sim';
import { GEM_BONUSES, GEM_POWER_BY_RANK, MAX_GEM_RANK, legendary } from './data';
import { GEM_POWER_PER_BONUS_RANK } from './gems.bonuses';
import type { EquippedGear, Gem, GemColor, GemPowerBonus, GemStash, Item } from './types';
import { GEM_COLORS, ITEM_SLOTS } from './types';

export { GEM_BONUS_RANKS, GEM_POWER_PER_BONUS_RANK, GEM_THRESHOLD_LIST, gemBonusLadder } from './gems.bonuses';

/** Gems fused into one of the next rank. */
export const GEMS_PER_FUSE = 3;

export const gemKey = (g: Gem): string => `${g.color}:${g.rank}`;

export function parseGemKey(k: string): Gem | null {
  const [color, r] = k.split(':');
  const rank = Number(r);
  if (!GEM_COLORS.includes(color as GemColor) || !Number.isInteger(rank) || rank < 1 || rank > MAX_GEM_RANK) return null;
  return { color: color as GemColor, rank };
}

/** Gem Power of one gem (legendary sockets multiply it). */
export function gemValue(g: Gem, mult = 1): number {
  return (GEM_POWER_BY_RANK[g.rank] ?? 0) * mult;
}

/** Socket multiplier of an item (2× on legendaries). */
export function socketMult(item: Item): number {
  return item.legendaryId ? (legendary(item.legendaryId)?.socketPowerMult ?? 1) : 1;
}

/** Total Gem Power per colour across equipped gear. */
export function gemPower(gear: EquippedGear): Record<GemColor, number> {
  const out: Record<GemColor, number> = { ruby: 0, sapphire: 0, emerald: 0 };
  for (const slot of ITEM_SLOTS) {
    const item = gear[slot];
    if (!item) continue;
    const mult = socketMult(item);
    for (const g of item.gems) if (g) out[g.color] += gemValue(g, mult);
  }
  return out;
}

/** Bonuses whose threshold is met, in table order (deterministic). */
export function activeGemBonuses(
  power: Record<GemColor, number>,
  classId: ClassId,
  table: readonly GemPowerBonus[] = GEM_BONUSES,
): GemPowerBonus[] {
  return table.filter((b) => (b.classId === null || b.classId === classId) && power[b.color] >= b.threshold);
}

/** Bonus ranks reached per colour (0..4) and Gem Power needed for the next one (null at max). */
export function gemProgress(power: Record<GemColor, number>, classId: ClassId, table: readonly GemPowerBonus[] = GEM_BONUSES): Record<GemColor, { rank: number; next: number | null; nextBonus: GemPowerBonus | null }> {
  const out = {} as Record<GemColor, { rank: number; next: number | null; nextBonus: GemPowerBonus | null }>;
  for (const color of GEM_COLORS) {
    const ladder = table.filter((b) => b.color === color && (b.classId === null || b.classId === classId)).sort((a, b) => a.threshold - b.threshold);
    const reached = ladder.filter((b) => power[color] >= b.threshold).length;
    const nextBonus = ladder[reached] ?? null;
    out[color] = { rank: reached, next: nextBonus ? nextBonus.threshold : null, nextBonus };
  }
  return out;
}

/** Bonus rank of a Gem Power total, ignoring the table: floor(power / 120), max 4. */
export function bonusRankOf(power: number): number {
  return Math.min(4, Math.floor(power / GEM_POWER_PER_BONUS_RANK));
}

// ============================================================================
// Stash
// ============================================================================

export function gemCount(stash: GemStash, g: Gem): number {
  return stash[gemKey(g)] ?? 0;
}

export function addGem(stash: GemStash, g: Gem, n = 1): GemStash {
  const k = gemKey(g);
  return { ...stash, [k]: (stash[k] ?? 0) + n };
}

/** Remove n of a gem; null if the stash does not have them. Empty keys are dropped. */
export function takeGem(stash: GemStash, g: Gem, n = 1): GemStash | null {
  const k = gemKey(g);
  const have = stash[k] ?? 0;
  if (have < n) return null;
  const next = { ...stash, [k]: have - n };
  if (next[k] === 0) delete next[k];
  return next;
}

/** Stash as a sorted list (colour order, then rank descending). */
export function listGems(stash: GemStash): { gem: Gem; count: number }[] {
  const out: { gem: Gem; count: number }[] = [];
  for (const k of Object.keys(stash)) {
    const gem = parseGemKey(k);
    if (gem && (stash[k] ?? 0) > 0) out.push({ gem, count: stash[k]! });
  }
  return out.sort((a, b) => GEM_COLORS.indexOf(a.gem.color) - GEM_COLORS.indexOf(b.gem.color) || b.gem.rank - a.gem.rank);
}

/** Gold per fuse, per rank of the gems going in (rank 1 → 2 costs 25, rank 4 → 5 costs 100). */
export const FUSE_GOLD_PER_RANK = 25;
export function fuseGoldCost(rank: number): number {
  return FUSE_GOLD_PER_RANK * Math.max(1, rank);
}

export type FuseResult = { ok: true; stash: GemStash; made: Gem } | { ok: false; reason: 'notEnough' | 'maxRank' | 'rankLocked' };

/** Fuse 3 gems of one colour+rank into 1 of the next rank. `maxRank` = tier cap. */
export function fuse(stash: GemStash, color: GemColor, rank: number, maxRank: number = MAX_GEM_RANK): FuseResult {
  if (rank >= MAX_GEM_RANK) return { ok: false, reason: 'maxRank' };
  if (rank + 1 > maxRank) return { ok: false, reason: 'rankLocked' };
  const k = gemKey({ color, rank });
  if ((stash[k] ?? 0) < GEMS_PER_FUSE) return { ok: false, reason: 'notEnough' };
  const made: Gem = { color, rank: rank + 1 };
  const next = { ...stash, [k]: (stash[k] ?? 0) - GEMS_PER_FUSE };
  next[gemKey(made)] = (next[gemKey(made)] ?? 0) + 1;
  if (next[k] === 0) delete next[k];
  return { ok: true, stash: next, made };
}

/** Fuse as much as possible for a colour, lowest rank first, up to `maxRank`. */
export function fuseAll(stash: GemStash, color: GemColor, maxRank: number = MAX_GEM_RANK): { stash: GemStash; made: Gem[] } {
  let s = stash;
  const made: Gem[] = [];
  for (let rank = 1; rank < Math.min(maxRank, MAX_GEM_RANK); rank++) {
    for (;;) {
      const r = fuse(s, color, rank, maxRank);
      if (!r.ok) break;
      s = r.stash;
      made.push(r.made);
    }
  }
  return { stash: s, made };
}

// ============================================================================
// Sockets (pure, on one item)
// ============================================================================

export type SocketResult = { ok: true; item: Item; removed: Gem | null } | { ok: false; reason: 'badSocket' };

/** Put `gem` in socket `index` (null clears it). Returns the gem that was there, if any. */
export function setSocket(item: Item, index: number, gem: Gem | null): SocketResult {
  if (!Number.isInteger(index) || index < 0 || index >= item.sockets) return { ok: false, reason: 'badSocket' };
  const gems = Array.from({ length: item.sockets }, (_, i) => item.gems[i] ?? null);
  const removed = gems[index] ?? null;
  gems[index] = gem ? { ...gem } : null;
  return { ok: true, item: { ...item, gems }, removed };
}

/** Recommended gem colour by role: tanks sapphire, healers emerald, damage ruby. */
export const ROLE_GEM_COLOR: Readonly<Record<Role, GemColor>> = { tank: 'sapphire', healer: 'emerald', dps: 'ruby' };

/**
 * Bot gem setup: fill empty sockets (and replace lower-rank gems of the wrong colour or rank)
 * with the best gems of the role colour from `stash`, up to `maxRank`. Returns new gear, the
 * new stash and gems taken out of sockets (already back in the stash). Deterministic: slots in
 * ITEM_SLOTS order, best gems first.
 */
export function autoGem(gear: EquippedGear, stash: GemStash, color: GemColor, maxRank: number = MAX_GEM_RANK): { gear: EquippedGear; stash: GemStash; changed: boolean } {
  let s = stash;
  let changed = false;
  const out: EquippedGear = { ...gear };
  const bestAvailable = (): Gem | null => {
    for (let r = Math.min(maxRank, MAX_GEM_RANK); r >= 1; r--) if (gemCount(s, { color, rank: r }) > 0) return { color, rank: r };
    return null;
  };
  for (const slot of ITEM_SLOTS) {
    let item = out[slot];
    if (!item) continue;
    for (let i = 0; i < item.sockets; i++) {
      const cur = item.gems[i] ?? null;
      const best = bestAvailable();
      if (!best) break;
      const better = !cur || cur.color !== color || cur.rank < best.rank;
      if (!better) continue;
      s = takeGem(s, best)!;
      const r = setSocket(item, i, best);
      if (!r.ok) continue;
      item = r.item;
      if (r.removed) s = addGem(s, r.removed);
      changed = true;
    }
    out[slot] = item;
  }
  return { gear: out, stash: s, changed };
}
